import React, { useEffect, useState } from 'react';

// Splash screen — shown ONLY when the app is running in standalone PWA mode
// (i.e. the user tapped the icon from their home screen). In a normal
// browser it never renders. After 5 seconds it snaps off (no fade).
const SPLASH_DURATION_MS = 5000;

function isStandalonePWA() {
  if (typeof window === 'undefined') return false;
  // iOS Safari sets navigator.standalone when launched from home screen.
  if (window.navigator && window.navigator.standalone === true) return true;
  // Android Chrome (and desktop installed PWAs) match this media query.
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  return false;
}

export const SplashScreen = () => {
  // If we're not in PWA mode, don't render anything.
  const [visible, setVisible] = useState(() => isStandalonePWA());

  useEffect(() => {
    if (!visible) return undefined;
    const t = setTimeout(() => setVisible(false), SPLASH_DURATION_MS);
    return () => clearTimeout(t);
  }, [visible]);

  // One-time iOS PWA safe-area paint fix.
  //
  // iOS PWA standalone mode has a well-known WebKit bug: with
  // `viewport-fit=cover` set, the `env(safe-area-inset-bottom)` region
  // (the home-indicator strip at the very bottom of the screen) renders
  // as a SOLID WHITE STRIP on first paint, regardless of what the page
  // is supposed to be drawing there. It stays white until WebKit
  // receives a viewport-mutation signal that forces it to re-evaluate
  // the painted regions.
  //
  // HISTORICAL WORKING RECIPE (used to fix this same bug in a prior
  // version of the app):
  //
  //   1. Make the body TALLER than the viewport — `min-height: 101vh`.
  //   2. Also temporarily allow html / body to overflow scrollably
  //      (they're permanently `overflow: hidden` in our CSS).
  //   3. `window.scrollTo(0, 1)` — actually moves document scroll
  //      position by 1 px because body is now tall enough to scroll.
  //   4. Two animation frames later: revert all three.
  //
  //   The COMBINATION of body resize + real scroll event is what
  //   triggers iOS to repaint the safe-area-inset-bottom strip.
  //   A pure scroll without the body resize does nothing (no actual
  //   scroll happens because body fits viewport). A pure body resize
  //   without the scroll doesn't ALWAYS trigger the repaint either.
  //   BOTH ingredients are needed.
  //
  // MULTI-TRIGGER STRATEGY:
  //   iOS's first-paint timing is non-deterministic in PWA standalone
  //   mode — depending on the device, iOS version, and how the user
  //   launched the app, the safe-area paint can be committed anywhere
  //   between ~100 ms and ~3 s after the page becomes visible. A
  //   single-shot nudge that fires before iOS has decided the safe
  //   area is "stuck" can be no-op'd by WebKit's coalescing.
  //   Solution: fire the nudge FOUR TIMES across the 5 s splash window
  //   — at 100, 500, 1500, and 3000 ms. ALL four triggers happen
  //   behind the splash's black `position: fixed; inset: 0` overlay,
  //   so the user never sees ANY of the 1 px scroll glitches; whichever
  //   nudge happens to fire after iOS has committed the bad-paint
  //   state will unstick it for the rest of the session.
  //
  // PWA-ONLY: only runs when `visible` is true (which is itself only
  // true in standalone PWA mode — see the `useState` initialiser
  // above). Web users never have the bug AND never see the nudge.
  useEffect(() => {
    if (!visible || typeof window === 'undefined') return undefined;

    const performNudge = () => {
      try {
        const html = document.documentElement;
        const body = document.body;
        const savedHtmlOverflow = html.style.overflow;
        const savedBodyOverflow = body.style.overflow;
        const savedBodyMinHeight = body.style.minHeight;

        // Step 1+2: make body / html temporarily scrollable AND tall
        // enough that a real scroll can happen.
        html.style.overflow = 'auto';
        body.style.overflow = 'auto';
        body.style.minHeight = '101vh';

        // Step 3: real scroll event (1 px is enough — iOS only needs
        // ANY non-zero scroll to re-evaluate paint regions).
        window.scrollTo(0, 1);

        // Step 4: revert everything two animation frames later so iOS
        // has actually committed the scroll-induced repaint before we
        // tear down the temporary scrollable state.
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            window.scrollTo(0, 0);
            html.style.overflow = savedHtmlOverflow;
            body.style.overflow = savedBodyOverflow;
            body.style.minHeight = savedBodyMinHeight;
          });
        });
      } catch (_e) {
        /* no-op — paint nudge is best-effort, never block splash dismiss. */
      }
    };

    // Multi-shot nudge — see comment block above for rationale.
    const timers = [100, 500, 1500, 3000].map((delay) =>
      window.setTimeout(performNudge, delay),
    );

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="splash" role="presentation" data-testid="splash-screen">
      {/*
        Title sits at the top of the splash. Heart grows large beneath it,
        filling the space between the title and the credits block. Per user
        direction the heart is NOT vertically centered — it's positioned
        high so its top edge tucks just under the title, and it expands
        DOWN to roughly the previous heart-bottom position.
      */}
      <div className="splash__title" data-testid="splash-title">
        danya &amp; rachel
      </div>

      <svg
        className="splash__heart"
        viewBox="0 0 100 90"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          {/*
            Splash heart — restored to the original linear diagonal
            pink → purple → blue gradient against the splash's black
            backdrop. This is the "previous splash" the user explicitly
            asked to bring back.

            Colors are hardcoded (not theme tokens) so the splash always
            reads the same on every launch — it is an intentional branded
            moment, not a time-of-day surface.

            Direction: top-left (pink) → bottom-right (blue), traversing
            purple at the midpoint.
          */}
          <linearGradient id="splashHeartGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#ff7fb8" />
            <stop offset="50%"  stopColor="#b695ff" />
            <stop offset="100%" stopColor="#7fbdff" />
          </linearGradient>
        </defs>
        {/* H5 — Apple-style iOS heart (smoother iOS curves, slight bump
            at the top dip, softer overall silhouette than the previous
            geometric one). Picked by the user in round 6. */}
        <path
          fill="url(#splashHeartGradient)"
          d="M50 88
             C 20 70, 4 52, 4 30
             C 4 14, 16 6, 28 6
             C 40 6, 48 14, 50 24
             C 52 14, 60 6, 72 6
             C 84 6, 96 14, 96 30
             C 96 52, 80 70, 50 88 Z"
        />
      </svg>

      <div className="splash__credits">
        <div className="splash__line">for danya</div>
        <div className="splash__line">designed entirely by me using emergent ai</div>
        <div className="splash__gap" />
        <div className="splash__line">для дани</div>
        <div className="splash__line">разработано мной полностью на emergent ai</div>
        <div className="splash__gap" />
        <div className="splash__line">2026</div>
      </div>
    </div>
  );
};
